<template>
  <div style="width: 100%">
    <template v-if="legend">
      <v-row dense justify="start">
        <div class="pr-6 my-1">
          <v-avatar class="font-weight-bold title white--text" color="primary" size="32">
            {{ personnelFirstLetter }}
          </v-avatar>
          = {{ $t('teams.types.PERSONNEL') }}
        </div>
        <div class="my-1">
          <v-avatar class="font-weight-bold title white--text" color="secondary" size="32">
            {{ managementFirstLetter }}
          </v-avatar>
          = {{ $t('teams.types.MANAGEMENT') }}
        </div>
      </v-row>
    </template>

    <template v-else>
      <svg :id="`circle-svg-${k}`" :viewBox="`0 0 ${withoutLines ? 12 : 105} 10`" style="display: block">
        <defs>
          <g :id="`personnel-circle-${k}`">
            <circle class="personnel" cy="1" r="2.5"/>
            <text class="text" dominant-baseline="middle" text-anchor="middle" y="1.25">{{
                personnelFirstLetter
              }}
            </text>
          </g>
          <g :id="`management-circle-${k}`">
            <circle class="management" cy="1" r="2.5"/>
            <text class="text" dominant-baseline="middle" text-anchor="middle" y="1.25">{{
                managementFirstLetter
              }}
            </text>
          </g>

          <g :id="`disabled-personnel-circle-${k}`">
            <circle cy="1" fill="#ddd" r="2.5"/>
            <text class="text" dominant-baseline="middle" text-anchor="middle" y="1.25">{{
                personnelFirstLetter
              }}
            </text>
          </g>
          <g :id="`disabled-management-circle-${k}`">
            <circle cy="1" fill="#ddd" r="2.5"/>
            <text class="text" dominant-baseline="middle" text-anchor="middle" y="1.25">{{
                managementFirstLetter
              }}
            </text>
          </g>
        </defs>

        <template v-if="withoutLines">
          <g>
            <use v-if="showPersonnel" :href="`#personnel-circle-${k}`" :x="3" y="2"></use>
            <use v-else :href="`#disabled-personnel-circle-${k}`" :x="3" y="2"></use>
            <text :class="showPersonnel ? 'personnel' : ''" :x="3" class="value-text" dominant-baseline="middle"
                  text-anchor="middle" y="7">
              {{ personnelValue > -1 ? `${personnelValue}%` : '-' }}
            </text>
          </g>
          <g>
            <use v-if="showManagement" :href="`#management-circle-${k}`" :x="9" y="2"></use>
            <use v-else :href="`#disabled-management-circle-${k}`" :x="9" y="2"></use>
            <text
              :class="showManagement ? 'management' : ''"
              :x="9"
              class="value-text"
              dominant-baseline="middle"
              text-anchor="middle"
              y="7"
            >
              {{ managementValue > -1 ? `${managementValue}%` : '-' }}
            </text>
          </g>
        </template>

        <template v-if="!legend && !withoutLines">
          <g>
            <line
              :stroke="loading ? '#eaeaea' : $getScoreColorHex(0)"
              stroke-linecap="round"
              stroke-width="2"
              x1="1"
              x2="5"
              y1="5"
              y2="5"
            />
            <line :stroke="loading ? '#eaeaea' : $getScoreColorHex(0)" :x2="(scale[0] / 100) * 105" stroke-width="2"
                  x1="1" y1="5" y2="5"/>

            <line
              :stroke="loading ? '#eaeaea' : $getScoreColorHex(100)"
              stroke-linecap="round"
              stroke-width="2"
              x1="90"
              x2="104"
              y1="5"
              y2="5"
            />
            <line
              :stroke="loading ? '#eaeaea' : $getScoreColorHex(100)"
              :x1="(scale[1] / 100) * 105"
              stroke-width="2"
              x2="100"
              y1="5"
              y2="5"
            />
            <line
              :stroke="loading ? '#eaeaea' : $getScoreColorHex(50)"
              :x1="(scale[0] / 100) * 105"
              :x2="(scale[1] / 100) * 105"
              stroke-width="2"
              y1="5"
              y2="5"
            />
          </g>

          <!-- <g v-if="showPersonnel">
            <rect :width="personnelStandardDeviationValue * 2"
                  :x="(personnelValue + 2.5) - personnelStandardDeviationValue" class="personnel" height="0.5"
                  y="1.5"></rect>
          </g> -->
          <g v-if="showManagement && managementStandardDeviationValue > 0 && !loading">
            <rect
              :width="managementStandardDeviationValue"
              :x="managementValue + 2.5 - (managementStandardDeviationValue / 2)"
              class="management"
              height="0.5"
              y="3"
            ></rect>
            <text :x="managementValue + 2.5 - (managementStandardDeviationValue / 2)"
                  class="std-deviation-text management" dominant-baseline="middle" text-anchor="left"
                  y="2">
              {{ $t('reports.standard_deviation') }}: {{ managementStandardDeviationValue }}%
            </text>
          </g>

          <g v-if="showPersonnel && !loading">
            <use :href="`#personnel-circle-${k}`" :x="personnelValue + 2.5" style="transition: all 0.3s ease-in-out"
                 y="4"></use>
            <text :x="personnelValueTextX" class="value-text personnel" dominant-baseline="middle" text-anchor="middle"
                  y="9">
              {{ personnelValue }}%
            </text>
          </g>
          <g v-if="showManagement && !loading">
            <use :href="`#management-circle-${k}`" :x="managementValue + 2.5" style="transition: all 0.3s ease-in-out"
                 y="4"></use>
            <text :x="managementValueTextX" class="value-text management" dominant-baseline="middle"
                  text-anchor="middle" y="9">
              {{ managementValue }}%
            </text>
          </g>
        </template>
      </svg>
      <template v-if="withoutLines">
        <v-tooltip :key="`hover-element-${k}`" :activator="`#circle-svg-${k}`" top>
          <div style="max-width: 300px"
               v-html="$t(`reports.icon_tooltip_personnel`)"></div>
          <div style="max-width: 300px"
               v-html="$t(`reports.icon_tooltip_management`)"></div>
        </v-tooltip>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ReportPersonnelVsManagementChart',
  props: {
    name: {
      type: String,
      required: false,
    },

    category: {
      type: String,
      required: false,
    },
    legend: {
      type: Boolean,
      default: false,
    },
    surveys: {
      type: Array,
      required: false,
    },
    scale: {
      type: Array,
      default: () => [40, 60],
    },
    subcategory: {
      type: Object,
      required: false,
    },
    withoutLines: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const k = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    return {
      width: 1000,
      k: k,
      topElement: `#personnel-${this.k}`,
    };
  },
  computed: {
    title() {
      return this.subcategory?.children[this.name]?.nameText;
    },
    showManagement() {
      return this.managementValue > -1 && !isNaN(this.managementValue);
    },
    showPersonnel() {
      return this.personnelValue > -1 && !isNaN(this.personnelValue);
    },
    personnelFirstLetter() {
      return this.$t('teams.types.PERSONNEL').charAt(0).toUpperCase();
    },
    managementFirstLetter() {
      return this.$t('teams.types.MANAGEMENT').charAt(0).toUpperCase();
    },
    isSubcategory() {
      return !!this.subcategory;
    },
    personnelSurvey() {
      return this.surveys?.find(s => s.type === 'PERSONNEL');
    },
    managementSurvey() {
      return this.surveys?.find(s => s.type === 'MANAGEMENT');
    },
    personnelValue() {
      if (!this.personnelSurvey) return -1;
      let value;
      if (this.isSubcategory) {
        value = this.personnelSurvey?.calculation?.engineResponse?.lta?.[this.category]?.avg
          ?.find(avg => avg.name === this.subcategory?.engineName)
          ?.subdata?.find(d => d.name === this.name)?.value;
      } else {
        value = this.personnelSurvey?.calculation?.engineResponse?.lta?.[this.category]?.avg?.find(avg => avg.name === this.name)?.value;
      }
      if (isNaN(value)) return -1;
      return parseFloat(parseFloat(value).toFixed(0));
    },
    personnelStandardDeviationValue() {
      if (!this.personnelSurvey) return -1;
      let value;
      if (this.isSubcategory) {
        value = this.personnelSurvey?.calculation?.engineResponse?.lta?.[this.category]?.stddev
          ?.find(stddev => stddev.name === this.subcategory?.engineName)
          ?.subdata?.find(d => d.name === this.name)?.value;
      } else {
        value = this.personnelSurvey?.calculation?.engineResponse?.lta?.[this.category]?.stddev?.find(
          stddev => stddev.name === this.name
        )?.value;
      }
      if (isNaN(value)) return -1;
      return parseFloat(parseFloat(value).toFixed(0));
    },
    managementValue() {
      if (!this.managementSurvey) return -1;
      let value;
      if (this.isSubcategory) {
        value = this.managementSurvey?.calculation?.engineResponse?.oja?.[this.category]?.avg
          ?.find(avg => avg.name === this.subcategory?.engineName)
          ?.subdata?.find(d => d.name === this.name)?.value;
      } else {
        value = this.managementSurvey?.calculation?.engineResponse?.oja?.[this.category]?.avg?.find(avg => avg.name === this.name)?.value;
      }
      if (isNaN(value)) return -1;
      return parseFloat(parseFloat(value).toFixed(0));
    },
    managementStandardDeviationValue() {
      if (!this.personnelSurvey) return -1;
      let value;
      if (this.isSubcategory) {
        value = this.managementSurvey?.calculation?.engineResponse?.oja?.[this.category]?.stddev
          ?.find(stddev => stddev.name === this.subcategory?.engineName)
          ?.subdata?.find(d => d.name === this.name)?.value;
      } else {
        value = this.managementSurvey?.calculation?.engineResponse?.oja?.[this.category]?.stddev?.find(
          stddev => stddev.name === this.name
        )?.value;
      }
      if (isNaN(value)) return -1;
      //return parseFloat(parseFloat(value).toFixed(0));
      return value;
    },
    personnelValueTextX() {
      let offset = 0;
      let diff = this.managementValue - this.personnelValue;
      if (Math.abs(diff) <= 6) {
        offset = this.personnelValue < this.managementValue ? -2.5 + Math.abs(diff) / 2 : 2.5 - Math.abs(diff) / 2;
        // Adjust offset when close to the borders
        if (this.personnelValue > 96 || this.managementValue > 96)
          offset = this.personnelValue < this.managementValue ? -5.5 + Math.abs(diff) : 0;
        else if (this.personnelValue < 3 || this.managementValue < 3)
          offset = this.personnelValue < this.managementValue ? 0 : 3.5 - Math.abs(diff);
      }
      return this.personnelValue + 2.5 + offset - (this.personnelValue === 100 || this.managementValue === 100 ? 1 : 0);
    },
    managementValueTextX() {
      let offset = 0;
      let diff = this.managementValue - this.personnelValue;
      if (Math.abs(diff) <= 5) {
        offset = this.managementValue > this.personnelValue ? 2.5 - Math.abs(diff) / 2 : -2.5 + Math.abs(diff) / 2;
        // Adjust offset when close to the borders
        if (this.managementValue > 96 || this.personnelValue > 96)
          offset = this.managementValue > this.personnelValue ? 0 : -5.5 + Math.abs(diff);
        else if (this.managementValue < 3 || this.personnelValue < 3)
          offset = this.managementValue > this.personnelValue ? 3.5 - Math.abs(diff) : 0;
      }
      return this.managementValue + 2.5 + offset - (this.managementValue === 100 || this.personnelValue === 100 ? 1 : 0);
    },
  },
  methods: {
    topPersonnel() {
      this.topElement = `#personnel-${this.k}`;
    },
    topManagement() {
      this.topElement = `#management-${this.k}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.svg * {
  font-family: $body-font-family;
}

.g {
  width: 98%;
}

.header-text {
  font-weight: bold;
  font-size: 22px;
  font-family: $body-font-family;
  fill: #000;
}

.text {
  font-weight: bold;
  font-size: 3px;
  text-transform: uppercase;
  font-family: $body-font-family;
  fill: white;
}

.value-text {
  font-weight: bold;
  font-size: 2px;
  text-transform: uppercase;
  fill: #ccc;
  font-family: $body-font-family;
}

.std-deviation-text {
  font-weight: bold;
  font-size: 1.6px;
  text-transform: uppercase;
  fill: #ccc;
  font-family: $body-font-family;
}

.personnel {
  fill: var(--v-primary-base);
}

.management {
  fill: var(--v-secondary-base);
}
</style>
